// Modern in-app confirmation modal that replaces window.confirm.
//
// Provider mounts a single modal at the root. Components consume it via
// the useConfirm() hook:
//
//   const confirm = useConfirm();
//   const ok = await confirm({
//     title: "Reset preferences?",
//     message: "Your brand rules and price targets will return to defaults.",
//     confirmLabel: "Reset",
//     destructive: true,
//   });
//   if (ok) { ... }
//
// Returns a promise that resolves to true (confirm) / false (cancel,
// backdrop click, or Escape). Matches the rest of the GrabOrSkip aesthetic
// (heavy borders, sharp corners, drop shadow).

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Warning, X } from "@phosphor-icons/react";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // {title, message, confirmLabel, cancelLabel, destructive, resolve}
  const stateRef = useRef(state);
  stateRef.current = state;

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        title: opts?.title || "Are you sure?",
        message: opts?.message || "",
        confirmLabel: opts?.confirmLabel || "Confirm",
        cancelLabel: opts?.cancelLabel || "Cancel",
        destructive: !!opts?.destructive,
        resolve,
      });
    });
  }, []);

  const handle = (value) => {
    if (stateRef.current) {
      stateRef.current.resolve(value);
      setState(null);
    }
  };

  // Esc cancels, Enter confirms
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") handle(false);
      else if (e.key === "Enter") handle(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[10500] flex items-end sm:items-center justify-center p-0 sm:p-4 animate-[fadeIn_0.15s_ease-out]"
          data-testid="confirm-dialog"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => handle(false)}
            data-testid="confirm-backdrop"
          />
          {/* Sheet (mobile bottom-sheet) / Card (desktop) */}
          <div
            className="relative w-full sm:max-w-md bg-white border-t-4 sm:border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] animate-[slideUp_0.2s_ease-out]"
            onClick={(e) => e.stopPropagation()}
            data-testid="confirm-card"
          >
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-3 mb-3">
                {state.destructive ? (
                  <div className="w-9 h-9 border-2 border-black bg-[#FF1744] text-white flex items-center justify-center shrink-0">
                    <Warning size={18} weight="bold" />
                  </div>
                ) : null}
                <div className="flex-1 min-w-0">
                  <h2
                    className="font-heading text-lg sm:text-xl font-black uppercase tracking-tight leading-tight"
                    data-testid="confirm-title"
                  >
                    {state.title}
                  </h2>
                </div>
                <button
                  onClick={() => handle(false)}
                  className="p-1.5 -m-1.5 hover:bg-neutral-100 active:bg-neutral-200"
                  data-testid="confirm-close"
                  aria-label="Close"
                >
                  <X size={18} weight="bold" />
                </button>
              </div>

              {state.message && (
                <p
                  className="font-body text-sm text-neutral-700 mb-5 sm:mb-6 leading-relaxed"
                  data-testid="confirm-message"
                >
                  {state.message}
                </p>
              )}

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handle(false)}
                  className="py-3 px-4 bg-white text-black border-2 border-black font-heading text-sm font-bold uppercase tracking-widest active:bg-neutral-100 transition-colors"
                  data-testid="confirm-cancel"
                  autoFocus={state.destructive}
                >
                  {state.cancelLabel}
                </button>
                <button
                  onClick={() => handle(true)}
                  className={`py-3 px-4 border-2 border-black font-heading text-sm font-bold uppercase tracking-widest shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:translate-y-[1px] active:translate-x-[1px] active:shadow-none transition-all ${
                    state.destructive
                      ? "bg-[#FF1744] text-white"
                      : "bg-[#00E676] text-black"
                  }`}
                  data-testid="confirm-ok"
                  autoFocus={!state.destructive}
                >
                  {state.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Safe fallback so a forgotten provider doesn't crash the page — fall back
    // to native confirm and warn loudly in dev.
    if (typeof window !== "undefined" && window.console) {
      console.warn("useConfirm() called outside of <ConfirmProvider>. Falling back to window.confirm().");
    }
    return (opts) => Promise.resolve(window.confirm(opts?.message || opts?.title || "Are you sure?"));
  }
  return ctx;
}
